import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { format } from 'date-fns';

interface PaymentMetric {
  date: string;
  transaction_count: number;
  total_volume_cents: number;
  total_fees_cents: number;
  total_payouts_cents: number;
  successful_payments: number;
  failed_payments: number;
  refunded_payments: number;
}

interface PaymentMetricsChartProps {
  data: PaymentMetric[];
}

export function PaymentMetricsChart({ data }: PaymentMetricsChartProps) {
  const chartData = data.map(metric => ({
    date: format(new Date(metric.date), 'MMM dd'),
    volume: metric.total_volume_cents / 100,
    fees: metric.total_fees_cents / 100,
    payouts: metric.total_payouts_cents / 100,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Volume Trends</CardTitle>
        <CardDescription>Daily payment metrics over time</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorFees" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorPayouts" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--chart-3))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--chart-3))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="date" 
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis 
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value) => `$${value.toLocaleString()}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number) => [`$${value.toFixed(2)}`, '']}
            />
            <Area
              type="monotone"
              dataKey="volume"
              stroke="hsl(var(--primary))"
              fillOpacity={1}
              fill="url(#colorVolume)"
              name="Total Volume"
            />
            <Area
              type="monotone"
              dataKey="fees"
              stroke="hsl(var(--chart-2))"
              fillOpacity={1}
              fill="url(#colorFees)"
              name="Platform Fees"
            />
            <Area
              type="monotone"
              dataKey="payouts"
              stroke="hsl(var(--chart-3))"
              fillOpacity={1}
              fill="url(#colorPayouts)"
              name="Vendor Payouts"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
